import {
    BokehPass,
    Bone,
    CopyShader,
    EffectComposer,
    FXAAShader,
    LinearFilter,
    Mesh,
    MeshDepthMaterial,
    NoBlending,
    OutlinePass,
    PCFSoftShadowMap,
    RenderPass,
    RGBADepthPacking,
    ShaderPass,
    SAOPass,
    SAOShader,
    TAARenderPass,
    UnrealBloomPass,
    Vector2,
    Vector3,
    WebGLRenderer,
    WebGLRenderTarget,
    checkMobile
} from 'da-three';
import EventDispatcher from './../service/EventDispatcher.js';

function getInternetExplorerVersion() {
    let rv = -1;
    if (navigator.appName === 'Microsoft Internet Explorer') {
        const ua = navigator.userAgent;
        const re = new RegExp("MSIE ([0-9]{1,}[\.0-9]{0,})");
        if (re.exec(ua) != null)
            rv = parseFloat(RegExp.$1);
    }
    else if (navigator.appName === 'Netscape') {
        const ua = navigator.userAgent;
        const re = new RegExp("Trident/.*rv:([0-9]{1,}[\.0-9]{0,})");
        if (re.exec(ua) != null)
            rv = parseFloat(RegExp.$1);
    }
    return rv;
}

export default class RenderManager{
    constructor(mediator,config,sceneMan){
        this.mediator = mediator;
        this.config = config;
        this.sceneMan = sceneMan;
        this.scene = this.sceneMan.scene;
        this.camera = this.sceneMan.camera;
        
        this.postprocessingOn = this.config.features.usePostProcessing.default;
        
        this.dispatcher = new EventDispatcher();
        this.dispatcher.setEventTypes([
            'afterRender',
            'beforeRender'
        ]);
        
        // RENDERER
        this.renderer = new WebGLRenderer({ antialias: true, maxLights: 8, alpha: true });
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.renderer.setClearColor( 0x000000, 0 );
        this.renderer.sortObjects = false;
        this.renderer.gammaOutput = true;
        this.renderer.gammaFactor = 2.2;
               
        // TODO hotfix for performance here
        this.frameStep = Date.now();
        this.objectLoaded = false;
        this.minimumQuallity = false;
        this.frameTotal = 0;
        this.frameCount = 0;
        this.frameDrop = 0;
        this.minimumDownscaling = 0.6;
        this.targetFPS = 30;
        this.throttled = false;
        this.warmup = true;
        this.ratioLocked = false;
        
        // SHADOWS
        if (!checkMobile()) {
            this.loadShadow();
        }
        
        
        // POST PROCESSING
        this.postprocesses = {};
        this.initPostProcessing();
        this.initialRatio = this.renderer.getPixelRatio();

        this.renderer.animate(this.animate.bind(this));
        
        this.mediator.addEventListener('afterWindowResize',this.onWindowResize.bind(this));
        this.mediator.addEventListener('beforeObjectLoad', () => this.renderer.clear());
        this.mediator.addEventListener('objectComplete', this.onObjectComplete.bind(this));

        this.positions = {};
    }
    
    
    
    animate() {
        if (this.objectLoaded){
            this.checkFrameRate();
        }
        this.dispatcher.dispatch('beforeRender');

        this.render();		
        
        this.dispatcher.dispatch('afterRender');
    }
    
    
    
    checkFrameRate() {
        let time = Date.now();

        const duration = time - this.frameStep;
        // reset counter every 90 frames
        if (this.frameCount % 90 === 0) {
            this.frameTotal = duration;
            this.frameCount = 1;
        } else {
            this.frameTotal += duration;
            this.frameCount++;
        }
//        const avgFrameRate = 1000 / (this.frameTotal / this.frameCount);

        if (this.frameCount % 10 === 0) {
            let newRatio = this.renderer.getPixelRatio() * (1000 / this.targetFPS) / (this.frameTotal / this.frameCount);
            // correct ratio
            if (newRatio < this.initialRatio * this.minimumDownscaling) {
                newRatio = this.initialRatio * this.minimumDownscaling;
            } else if (newRatio > this.initialRatio) {
                newRatio = this.initialRatio;
            }
            let diff = Math.abs(newRatio - this.renderer.getPixelRatio());
            if (!this.ratioLocked) {
                if (newRatio < this.initialRatio * this.minimumDownscaling) {
                    newRatio = this.initialRatio * this.minimumDownscaling;
                } else if (newRatio > this.initialRatio) {
                    newRatio = this.initialRatio;
                }

                if (diff > 0.1) { 
                    this.ratioLocked = true;
                    if (this.config.tech.debug) console.log("DEBUG ===== New Ratio", newRatio, "last Distance", Math.abs(newRatio - this.renderer.getPixelRatio()));
                    this.renderer.setPixelRatio(newRatio);
                }
            } else {
                // TODO experiment with threshold values
                if (diff > 0.2) {
                    // 
                    this.frameDrop++;
                } else {
                    this.frameDrop = 0;
                }

                if (this.frameDrop > 5) {
                    if (this.config.tech.debug) console.log("DEBUG ===== unlocking pixel ratio");
                    this.ratioLocked = false;
                    this.frameDrop = 0;
                }
            }
        }

        this.frameStep = time;
    }
    
    
    
    initPostProcessing(){
        if (this.config.features.usePostProcessing.active){
            let pp = this.config.postprocess;
            //Composer and render pass (required by all postprocessing)
            this.composer = new EffectComposer( this.renderer );
            this.composer.setSize(window.innerWidth, window.innerHeight);            
            this.composer.addPass( new RenderPass( this.scene, this.camera ) );


            //Temporal antialiasing (strong performance hit)
            if (this.config.postprocess.taa !== undefined){
                let taaRenderPass = new TAARenderPass( this.scene, this.camera );
                taaRenderPass.unbiased = pp.taa.settings.unbiased;
                taaRenderPass.sampleLevel = pp.taa.settings.sampleLevel;
                this.composer.addPass( taaRenderPass );
                this.postprocesses.taa = taaRenderPass;
            }


            //SSAO (Experimental - does not work well)
            if (this.config.postprocess.ssao !== undefined && getInternetExplorerVersion() !== 11 && !checkMobile()) {
                let saoRenderPass = new RenderPass(this.scene, this.camera);
                //composer.addPass(ssaoPass);
                let saoPass = new SAOPass(this.scene, this.camera, false, true);
                saoPass.renderToScreen = false;
                saoPass.params.saoBias = 0.1;
                saoPass.params.saoIntensity = 0.12;
                saoPass.params.saoScale = 6000;
                saoPass.params.saoKernelRadius = 12;
                saoPass.params.saoMinResolution = 0;
                saoPass.params.saoBlur = true;
                saoPass.params.saoBlurRadius = 3;
                saoPass.params.saoBlurStdDev = 2;
                saoPass.params.saoBlurDepthCutoff = 0.01;

                console.log(saoPass.params);
                this.composer.addPass(saoPass);
                this.config.postprocess.ssao = saoPass;
            }
            
            
            //Unreal bloom
            if (this.config.postprocess.bloom !== undefined){
                let bloomThreshhold = pp.bloom.settings.bloomThreshhold;
                let bloomStrength = pp.bloom.settings.bloomStrength;
                let bloomRadius = pp.bloom.settings.bloomRadius;
                let bloomPass = new UnrealBloomPass(new Vector2(window.innerWidth, window.innerHeight), bloomStrength, bloomRadius, bloomThreshhold);//1.0, 9, 0.5, 512);            
                this.composer.addPass(bloomPass);
                this.postprocesses.bloom = bloomPass;
    //            this.renderer.gammaInput = true;
    //            this.renderer.gammaOutput = true;
            }


            //Object outline
            if (this.config.postprocess.edgeglow !== undefined){
                let outlinePass = new OutlinePass( new Vector2(window.innerWidth, window.innerHeight), this.scene, this.camera);
                outlinePass.edgeStrength = pp.edgeglow.settings.edgeStrength;
                outlinePass.edgeGlow = pp.edgeglow.settings.edgeGlow;
                outlinePass.edgeThickness = pp.edgeglow.settings.edgeThickness;
                outlinePass.pulsePeriod = pp.edgeglow.settings.pulsePeriod;
                this.composer.addPass( outlinePass );
                this.postprocesses.edgeglow = outlinePass;
            }

            //Effect FXAA
            if (this.config.postprocess.fxaa !== undefined){
                let effectFXAA = new ShaderPass(FXAAShader);
                effectFXAA.uniforms['resolution'].value.set(1 / window.innerWidth, 1 / window.innerHeight );
                //this.effectFXAA.renderToScreen = true;
                this.composer.addPass(effectFXAA);
                this.postprocesses.fxaa = effectFXAA;
            }
            
            //Copy shader (required by all postprocessing)
            let copyShader = new ShaderPass(CopyShader);
            copyShader.renderToScreen = true;
            this.composer.addPass(copyShader);
            
            //DOF (Experimental - does not work well)
            if (this.config.postprocess.dof !== undefined){
                let bokehPass = new BokehPass( this.scene, this.camera, {
                        focus: 		pp.dof.settings.focus,
                        aperture:	pp.dof.settings.aperture,
                        maxblur:	pp.dof.settings.maxblur,
                        width: window.innerWidth,
                        height: window.innerHeight
                } );
                bokehPass.renderToScreen = true;
                this.composer.addPass(bokehPass);
                this.postprocesses.dof = bokehPass;
            }
        }
    }
    
    
    
    loadShadow() {
        this.renderer.shadowMap.enabled = true;
        this.renderer.shadowMapSoft = true;
        this.renderer.shadowMap.type = PCFSoftShadowMap;
        this.renderer.physicallyBasedShading = true;
    }
    
    
    
    onObjectComplete(event,object){
        this.frameStep = Date.now();
        this.objectLoaded = true;
    }
    
    
    
    onWindowResize(){
        let SCREEN_WIDTH = window.innerWidth;
        let SCREEN_HEIGHT = window.innerHeight;

        this.renderer.setSize(SCREEN_WIDTH, SCREEN_HEIGHT);
        this.renderer.setPixelRatio(window.devicePixelRatio);
        
        if (this.config.features.usePostProcessing.active) {
            this.composer.setSize( SCREEN_WIDTH, SCREEN_HEIGHT );
            if (this.postprocesses.fxaa !== undefined){
                this.postprocesses.fxaa.uniforms['resolution'].value.set(1 / SCREEN_WIDTH, 1 / SCREEN_HEIGHT );
            }
            if (this.postprocesses.ssao !== undefined){
                this.postprocesses.ssao.uniforms[ 'size' ].value.set( SCREEN_WIDTH, SCREEN_HEIGHT );
                this.depthRenderTarget.setSize( SCREEN_WIDTH, SCREEN_HEIGHT );
            }
        }
    }
    
    
    
    removeEdgeGlow() {
        if (this.postprocesses.edgeglow !== undefined) {
            this.postprocesses.edgeglow.selectedObjects = [];
        }
    }
    
    
    
    render() {
        this.renderer.clear();
        
        if (this.config.features.useVideo.active && this.video){
            if ( this.video.readyState === this.video.HAVE_ENOUGH_DATA ){
                this.movieScreen.visible = true;
                this.videoImageContext.drawImage( this.video, 0, 0, this.videoImage.width, this.videoImage.height );
                if ( this.videoTexture ) {
                    this.videoTexture.needsUpdate = true;
                }
            }else{
                this.movieScreen.visible = false;
            }
            
            //Update reflective cubemap
            if (this.sceneMan.group && this.config.features.useSkyBox.active){
                const scope = this.sceneMan;
                scope.group.traverse(function(node){
                    if (node.id !== scope.skyBox.id){
                        node.visible = false;
                    }
                });
                scope.mirrorCubeCamera.update( this.renderer, this.scene );
                scope.group.traverse(function(node){
                    node.visible = true;
                });
            }
        }

        // test TODO remove

        this.sceneMan.group.traverse((node) => {
            if(node instanceof Bone && node.name === 'Top_Case') {
                node.updateMatrix();
                const wp = new Vector3();
                wp.setFromMatrixPosition(node.matrix);

                if (!this.positions[node.uuid]) {
                    this.positions[node.uuid] = wp;
                } else {
                    // check if position is different!
                    const p = this.positions[node.uuid];
                    //const eq = node.position.x === p.x && node.position.y === p.y && node.position.z === p.z;
                    if (!p.equals(wp)) {
                        //console.log("New Position of", node.name, "Position:",wp);
                        // save new pos
                        this.positions[node.uuid] = wp;
                    }
                }
            }
        });

        if (this.postprocessingOn){
            if (this.postprocesses.ssao !== undefined){
                this.scene.overrideMaterial = this.depthMaterial;
                this.renderer.render( this.scene, this.camera, this.depthRenderTarget, true );
                this.scene.overrideMaterial = null;
            }
            this.composer.render();
        }else{
            this.renderer.render( this.scene, this.camera );
        }
    }
    
    
    
    setPostprocessingOn(active){
        this.postprocessingOn = active;
    }
    
    
    
    togglePostprocess(name){
        if (this.postprocesses[name] !== undefined ) {
            const newValue = !this.postprocesses[name].enabled;
            this.postprocesses[name].enabled = newValue;
        }
    }
};


// WEBPACK FOOTER //
// ./src/js/studio/manager/RenderManager.js